import { makeAutoObservable } from 'mobx';
import { token } from '@/infrastructure/container';
import { type IAnalytics } from '@/services/Analytics';
import { type IAppBus } from '@/services/AppBus';
import { type IBomCompute } from '@/services/BomCompute';
import { type IBundleAssembler, type AssemblerSlotInput } from '@/services/BundleAssembler';
import { type IClock } from '@/services/Clock';
import { type ILogger } from '@/services/Logger';
import { type LocaleStore } from './LocaleStore';
import { DomainError } from '@/domain/errors';
import {
  type QuarantineRow,
  type SimulationPhase,
  type SimulationResult,
  type SourceKind,
} from '@/domain/beforered';
import { type ImportsStore } from './ImportsStore';
import { type SettingsStore } from './SettingsStore';
import { type WarningsStore } from './WarningsStore';

export class SimulationStore {
  private _phase: SimulationPhase = 'idle';
  private _quarantine: QuarantineRow[] = [];
  private _result: SimulationResult | null = null;
  private _errorMessage = '';
  private _durationMs = 0;
  // Stale-run guard: bumped on reset/cancel/new run; trailing writes check `isCurrent`.
  private _runGen = 0;

  constructor(
    private bus: IAppBus,
    private clock: IClock,
    private logger: ILogger,
    private assembler: IBundleAssembler,
    private compute: IBomCompute,
    private imports: ImportsStore,
    private settings: SettingsStore,
    private warnings: WarningsStore,
    private analytics: IAnalytics,
    private locale: LocaleStore,
  ) {
    makeAutoObservable(this);
  }

  get phase(): SimulationPhase {
    return this._phase;
  }
  get result(): SimulationResult | null {
    return this._result;
  }
  get quarantine(): readonly QuarantineRow[] {
    return this._quarantine;
  }
  get errorMessage(): string {
    return this._errorMessage;
  }
  get durationMs(): number {
    return this._durationMs;
  }

  get isRunning(): boolean {
    return (
      this._phase === 'expanding-bom' ||
      this._phase === 'seeding-demand' ||
      this._phase === 'simulating'
    );
  }

  get hasResult(): boolean {
    return this._phase === 'done' && this._result !== null;
  }

  reset = (): void => {
    this._runGen++;
    this._phase = 'idle';
    this._quarantine = [];
    this._result = null;
    this._errorMessage = '';
    this._durationMs = 0;
  };

  run = async (): Promise<void> => {
    if (this.isRunning) return;
    if (!this.imports.isReadyToRun) return;
    const gen = ++this._runGen;
    const isCurrent = (): boolean => gen === this._runGen;
    const start = this.clock.now();
    this._phase = 'expanding-bom';
    this._quarantine = [];
    this._result = null;
    this._errorMessage = '';
    this.warnings.clear();
    this.bus.simulationPhase({ phase: this._phase, skusProcessed: 0, skusTotal: 0 });

    try {
      const slots: AssemblerSlotInput[] = [];
      for (const source of ['BOM', 'INVENTORY', 'SALES', 'MFG', 'PURCHASE', 'PARTS'] as SourceKind[]) {
        const slot = this.imports.slot(source);
        if (slot.isValid && slot.parsed) {
          slots.push({
            source,
            rows: slot.parsed.rows,
            mapping: slot.mapping,
          });
        }
      }

      if (!isCurrent()) return;
      this._phase = 'seeding-demand';
      this.bus.simulationPhase({ phase: 'seeding-demand', skusProcessed: 0, skusTotal: 0 });

      const { bundle, quarantine } = this.assembler.assemble(slots, this.settings.today);

      if (quarantine.length > 0) {
        this.bus.validationWarning({
          source: quarantine[0].source,
          message: `${quarantine.length} row${quarantine.length === 1 ? '' : 's'} quarantined`,
          count: quarantine.length,
        });
      }

      if (!isCurrent()) return;
      this._phase = 'simulating';
      this._quarantine = quarantine;
      this.bus.simulationPhase({ phase: 'simulating', skusProcessed: 0, skusTotal: 0 });

      const result = this.compute.simulate(bundle, {
        today: this.settings.today,
        horizonDays: this.settings.horizonDays,
        defaultPurchaseLeadDays: this.settings.defaultPurchaseLeadDays,
        defaultProductionLeadDays: this.settings.defaultProductionLeadDays,
        safetyStockPercent: this.settings.safetyStockPercent,
      });

      if (!isCurrent()) return;
      const duration = this.clock.now() - start;
      this._result = result;
      this._phase = 'done';
      this._durationMs = duration;
      this.analytics.run({
        durationMs: duration,
        recommendationCount: result.recommendations.length,
        slotCount: slots.length,
        quarantineCount: quarantine.length,
      });
      await this.bus.simulationCompleted({
        recommendationCount: result.recommendations.length,
        durationMs: duration,
      });
    } catch (cause) {
      this.logger.error('SimulationStore.run failed', cause);
      if (!isCurrent()) return;
      this._phase = 'error';
      this._errorMessage = cause instanceof Error ? cause.message : String(cause);
      // Enum-grade tag only — the raw message can contain CSV-derived text and must not leave.
      this.analytics.error(
        cause instanceof DomainError ? cause.tag : cause instanceof Error ? cause.name : 'unknown',
      );
      this.bus.toast(
        'error',
        `${this.locale.messages.toasts.simFailed}: ${this._errorMessage}`,
      );
    }
  };

  cancel = (): void => {
    if (!this.isRunning) return;
    this._runGen++;
    this._phase = 'cancelled';
    this.bus.simulationPhase({ phase: 'cancelled', skusProcessed: 0, skusTotal: 0 });
  };
}

export const TOKEN_SimulationStore = token<SimulationStore>('SimulationStore');
